import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { ArrowRight, Loader2, PartyPopper, Sparkles, X } from "lucide-react";

const Ring = ({ pct }) => {
  const r = 30, c = 2 * Math.PI * r;
  return (
    <svg width="76" height="76" viewBox="0 0 76 76" className="-rotate-90">
      <circle cx="38" cy="38" r={r} fill="none" stroke="#E2E8F0" strokeWidth="7"/>
      <motion.circle cx="38" cy="38" r={r} fill="none" stroke="url(#ob-ring-g)" strokeWidth="7" strokeLinecap="round"
        strokeDasharray={c} initial={{ strokeDashoffset: c }}
        animate={{ strokeDashoffset: c * (1 - pct / 100) }}
        transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}/>
      <defs><linearGradient id="ob-ring-g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#14B8A6"/><stop offset="100%" stopColor="#1E1B4B"/>
      </linearGradient></defs>
    </svg>
  );
};

export const OnboardingChecklist = () => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/onboarding"); if (r.data.show) setData(r.data); }
      catch { /* offline / transient — widget simply hidden */ }
    })();
  }, []);

  if (!data || hidden) return null;

  const dismiss = async () => {
    setHidden(true);
    try { await api.post("/onboarding/dismiss"); } catch { /* best-effort */ }
  };

  const pct = Math.round((data.done / data.total) * 100);
  const next = data.items.find((i) => !i.done);

  const onContinue = async () => {
    if (next.action === "create_inspection") {
      setBusy(true);
      try {
        const now = new Date().toISOString();
        const r = await api.post("/inspections", {
          title: `Inspection — ${new Date().toLocaleString()}`,
          items: { inspection_datetime: { answer: now, notes: "", photos: [] } },
        });
        navigate(`/inspections/${r.data.id}`);
      } catch { navigate("/"); }
      finally { setBusy(false); }
    } else navigate(next.link);
  };

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
        data-testid="onboarding-widget"
        className="mb-8 bg-white border-2 border-slate-200 shadow-[6px_6px_0_0_#1E1B4B]">
        {data.all_done ? (
          <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-[76px] h-[76px] shrink-0 bg-teal-50 border-2 border-teal-200 flex items-center justify-center mx-auto sm:mx-0">
              <PartyPopper size={32} className="text-teal-600" strokeWidth={2}/>
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-teal-600">Setup complete</p>
              <p className="font-black text-slate-900 text-lg leading-tight">You&apos;re all set — nice work!</p>
              <p className="text-xs text-slate-500 mt-0.5">You&apos;ve seen the full workflow. Keep every Pro feature — AI reports, statutory clocks and unlimited inspections.</p>
            </div>
            <div className="flex sm:flex-col items-center sm:items-end justify-center gap-2 shrink-0">
              <button onClick={() => navigate("/pricing")} data-testid="onboarding-upgrade-btn"
                className="h-11 px-5 inline-flex items-center gap-2 bg-[#1E1B4B] text-white font-bold text-sm border-2 border-[#1E1B4B] hover:bg-[#0F0E2E]">
                <Sparkles size={15} strokeWidth={2.5}/> Upgrade to Pro
              </button>
              <button onClick={dismiss} data-testid="onboarding-skip"
                className="text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-900">Dismiss</button>
            </div>
          </div>
        ) : (
          <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="relative shrink-0 mx-auto sm:mx-0">
              <Ring pct={pct} />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span data-testid="onboarding-progress" className="text-lg font-black text-slate-900 leading-none">{pct}%</span>
                <span className="text-[8px] font-bold uppercase tracking-wider text-slate-400">setup</span>
              </div>
            </div>
            <div className="flex-1 min-w-0 text-center sm:text-left">
              <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-teal-600">Next step · {data.done} of {data.total} done</p>
              <p className="font-black text-slate-900 text-lg leading-tight" data-testid="onboarding-next-label">{next.label}</p>
              <p className="text-xs text-slate-500 truncate">{next.hint}</p>
              <div className="flex gap-1.5 mt-2.5 justify-center sm:justify-start">
                {data.items.map((it) => (
                  <div key={it.id} title={`${it.label}${it.done ? " ✓" : ""}`} data-testid={`onboarding-item-${it.id}`}
                    className={`h-1.5 flex-1 max-w-[42px] transition-colors ${it.done ? "bg-teal-500" : it === next ? "bg-[#1E1B4B]" : "bg-slate-200"}`}/>
                ))}
              </div>
            </div>
            <div className="flex sm:flex-col items-center sm:items-end justify-center gap-2 shrink-0">
              <button onClick={onContinue} disabled={busy} data-testid="onboarding-continue-btn"
                className="h-11 px-5 inline-flex items-center gap-2 bg-[#1E1B4B] text-white font-bold text-sm border-2 border-[#1E1B4B] hover:bg-[#0F0E2E] disabled:opacity-60 group">
                {busy ? <Loader2 size={15} className="animate-spin"/> : <>Continue <ArrowRight size={15} strokeWidth={3} className="transition-transform group-hover:translate-x-0.5"/></>}
              </button>
              <button onClick={dismiss} data-testid="onboarding-skip"
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 hover:text-slate-900">
                <X size={11} strokeWidth={3}/> Skip setup
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingChecklist;
